import React from 'react';
import { Link } from 'react-router-dom';

export default function FeaturesPage() {
  const DFY_PRICE = 499;
  const SELF_SERVE_PRICE = 299;
  const CREDIT = 200;

  return (
    <div className="p-8 max-w-5xl mx-auto">

      {/* === HERO === */}
      <header className="mb-10 text-center">
        <h1 className="text-3xl sm:text-4xl font-bold mb-3">
          Reduce order-status tickets by 20–40% — live in 48–72 hours.
        </h1>

        <p className="text-gray-700 max-w-2xl mx-auto mb-4">
          Product-aware AI + self-serve order lookup. Grounded answers from your content,
          not hallucinations. See real results before you commit.
        </p>

        <div className="mt-2 mb-6 inline-flex items-center gap-2 bg-indigo-50 border border-indigo-200 rounded-full px-4 py-2 text-sm text-indigo-800">
          <span className="font-semibold">📊 Pilot snapshot:</span>
          28% fewer tickets • +6% add-to-cart • 48h deploy
        </div>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to="/pilot?variant=dfy"
            className="px-6 py-3 bg-indigo-600 text-white rounded-lg shadow-md hover:bg-indigo-700 font-medium"
          >
            Start DFY Pilot — ${DFY_PRICE}
          </Link>

          <Link
            to="/pilot?variant=self-serve"
            className="px-5 py-2 border border-gray-300 rounded text-sm hover:bg-gray-50"
          >
            Self-Serve Pilot — ${SELF_SERVE_PRICE}
          </Link>

          <Link
            to="/demo/rag?mode=dtc_supplements"
            className="px-5 py-2 text-sm text-indigo-600 hover:underline"
          >
            Try live demo
          </Link>
        </div>

        <p className="mt-4 text-xs text-gray-500">
          Upgrade within 30 days and receive a <strong>${CREDIT}</strong> credit toward Pro.
        </p>
      </header>

      {/* === WHAT YOU GET === */}
      <section className="mb-10">
        <h2 className="text-2xl font-semibold mb-4 text-center">
          What the pilot actually does
        </h2>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="p-4 border rounded bg-white">
            <h3 className="font-semibold mb-2">Order lookup (first line of defense)</h3>
            <p className="text-sm text-gray-700">
              Customers check order status, tracking, and items instantly — no ticket required.
              Typically resolves 20–40% of support volume alone.
            </p>
          </div>

          <div className="p-4 border rounded bg-white">
            <h3 className="font-semibold mb-2">Product-aware answers</h3>
            <p className="text-sm text-gray-700">
              Recommendations, sizing, ingredients, policies — all grounded in your actual content.
            </p>
          </div>

          <div className="p-4 border rounded bg-white">
            <h3 className="font-semibold mb-2">FAQ quick-match layer</h3>
            <p className="text-sm text-gray-700">
              Common questions answered instantly without hitting the model — faster and 50–70% cheaper.
            </p>
          </div>

          <div className="p-4 border rounded bg-white">
            <h3 className="font-semibold mb-2">Escalation when needed</h3>
            <p className="text-sm text-gray-700">
              Sends conversation + suggested reply to your support team (Slack, email, or helpdesk).
            </p>
          </div>
        </div>
      </section>

      {/* === HOW IT WORKS === */}
      <section className="mb-10 bg-indigo-50 border-l-4 border-indigo-600 p-5 rounded">
        <h2 className="text-xl font-semibold mb-3">How the 48–72h pilot works</h2>

        <ol className="list-decimal pl-5 text-sm text-gray-700 space-y-2">
          <li><strong>Submit content:</strong> 3–10 URLs or a simple CSV</li>
          <li><strong>We build your dataset:</strong> cleaned, structured, optimized for answers</li>
          <li><strong>Deploy:</strong> private demo + optional widget for your site</li>
          <li><strong>Test:</strong> ask real customer questions</li>
          <li><strong>Review:</strong> get ROI snapshot + next steps</li>
        </ol>
      </section>

      {/* === PILOT OPTIONS === */}
      <section className="mb-10">
        <h2 className="text-2xl font-semibold mb-4">Choose your pilot</h2>

        <div className="grid gap-4 md:grid-cols-2">

          {/* DFY */}
          <div className="p-5 border rounded bg-white shadow-sm">
            <h3 className="text-lg font-semibold mb-2">Done-for-you pilot</h3>

            <div className="text-2xl font-bold mb-2">
              ${DFY_PRICE}
            </div>

            <ul className="text-sm text-gray-700 list-disc pl-5 space-y-1 mb-4">
              <li>We handle setup, ingestion, and tuning</li>
              <li>Best performance and fastest results</li>
              <li>Includes order lookup + FAQ layer</li>
              <li>ROI snapshot included</li>
            </ul>

            <Link
              to="/pilot?variant=dfy"
              className="block text-center px-4 py-2 bg-indigo-600 text-white rounded"
            >
              Start DFY Pilot
            </Link>
          </div>

          {/* SELF SERVE */}
          <div className="p-5 border rounded bg-white">
            <h3 className="text-lg font-semibold mb-2">Self-serve pilot</h3>

            <div className="text-2xl font-bold mb-2">
              ${SELF_SERVE_PRICE}
            </div>

            <ul className="text-sm text-gray-700 list-disc pl-5 space-y-1 mb-4">
              <li>Lower-cost entry point</li>
              <li>Upload CSV or URLs</li>
              <li>Live demo delivered in 48h</li>
              <li>Upgrade anytime with credit</li>
            </ul>

            <Link
              to="/pilot?variant=self-serve"
              className="block text-center px-4 py-2 border rounded"
            >
              Start Self-Serve
            </Link>
          </div>

        </div>
      </section>

      {/* === PRICING AFTER PILOT === */}
      <section className="mb-10 border rounded p-5 bg-white">
        <h2 className="text-xl font-semibold mb-3">Simple monthly plans</h2>

        <div className="grid gap-4 md:grid-cols-3">

          <div className="p-4 border rounded">
            <div className="text-sm text-gray-500">Starter</div>
            <div className="text-xl font-bold">$79/mo</div>
            <ul className="text-sm mt-2 list-disc pl-5 text-gray-700">
              <li>Self-serve setup</li>
              <li>FAQ + order lookup</li>
              <li>Basic support</li>
            </ul>
          </div>

          <div className="p-4 border rounded bg-indigo-50">
            <div className="text-sm text-gray-500">Growth</div>
            <div className="text-xl font-bold">$299/mo</div>
            <ul className="text-sm mt-2 list-disc pl-5 text-gray-700">
              <li>DFY onboarding</li>
              <li>Escalation webhook</li>
              <li>Monthly tuning</li>
            </ul>
          </div>

          <div className="p-4 border rounded">
            <div className="text-sm text-gray-500">Pro</div>
            <div className="text-xl font-bold">$999/mo</div>
            <ul className="text-sm mt-2 list-disc pl-5 text-gray-700">
              <li>Full catalog ingestion</li>
              <li>Advanced integrations</li>
              <li>Priority support</li>
            </ul>
          </div>

        </div>

        <p className="mt-4 text-sm text-gray-600">
          Pilot fee is credited when you upgrade within 30 days.
        </p>
      </section>

      {/* === FINAL CTA === */}
      <div className="text-center">
        <h3 className="text-lg font-semibold mb-2">
          See it on your store in 48–72 hours
        </h3>

        <div className="flex flex-col sm:flex-row gap-3 justify-center mt-3">
          <Link
            to="/pilot?variant=dfy"
            className="px-5 py-2 bg-indigo-600 text-white rounded"
          >
            Start DFY Pilot
          </Link>

          <Link
            to="/demo/rag?mode=dtc_supplements"
            className="px-5 py-2 border rounded"
          >
            Try demo first
          </Link>
        </div>
      </div>

    </div>
  );
}